/**
 * CSV取込行と既存の支払予定(planned)の突合(消込)。
 *
 * docs/specs/core-schema.md 3.2 の確定ルール:
 *  - 同一口座・同一direction・**金額一致**・**日付が±5営業日以内**の planned 行を候補にする
 *  - 候補が1件なら自動消込(新規行は作らず、既存行を settled に更新して実日付・摘要で上書き)
 *  - 候補が複数または0件なら画面で人が選ぶ(消込 or 新規登録)
 *  - dedup_hash が既存の settled 行と一致する取込行はスキップし、重複件数として表示する
 *
 * 二重計上を防ぐことが目的。経費の確定で作られた支払予定と、口座の実際の出金を1本にまとめる。
 */
import { addBusinessDays } from '@core/lib/date';
import type { CashEntry, DateStr } from '@core/lib/types';
import type { ParsedRow } from './csv';

/** 突合の許容範囲(営業日) */
export const MATCH_WINDOW_BUSINESS_DAYS = 5;

/** 重複判定キー: 口座+日付+方向+金額+摘要(空白差を無視) */
export function dedupHash(params: {
  cashAccountId: string;
  date: DateStr;
  direction: 'in' | 'out';
  amount: number;
  description: string;
}): string {
  const normalized = [
    params.cashAccountId,
    params.date,
    params.direction,
    String(params.amount),
    params.description.replace(/\s+/g, '').toLowerCase(),
  ].join('|');
  // 文字列をそのまま入れると長くなるためFNV-1aで短縮する
  let hash = 0x811c9dc5;
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(16)}-${normalized.length}`;
}

/** 取込行の日付から±5営業日の範囲を返す */
export function matchWindow(date: DateStr): { from: DateStr; to: DateStr } {
  return {
    from: addBusinessDays(date, -MATCH_WINDOW_BUSINESS_DAYS),
    to: addBusinessDays(date, MATCH_WINDOW_BUSINESS_DAYS),
  };
}

export function findCandidates(row: ParsedRow, planned: CashEntry[]): CashEntry[] {
  const { from, to } = matchWindow(row.date);
  return planned.filter(
    (p) => p.direction === row.direction && p.amount === row.amount && p.date >= from && p.date <= to,
  );
}

export type PlanKind = 'auto-match' | 'needs-review' | 'new' | 'duplicate';

export type ImportPlanItem = {
  row: ParsedRow;
  kind: PlanKind;
  /** auto-match のときの相手、needs-review のときの候補群 */
  candidates: CashEntry[];
  /** 画面で人が選んだ結果(消込先の入出金ID / null なら新規登録) */
  chosenEntryId?: string | null;
  hash: string;
};

/**
 * 取込計画を作る。ここでは書き込みを行わない(画面で確認してから実行する)。
 * 1つの planned 行が複数の取込行に消し込まれないよう、自動消込で使った行は候補から外す。
 */
export function buildImportPlan(params: {
  cashAccountId: string;
  rows: ParsedRow[];
  planned: CashEntry[];
  settledHashes: Set<string>;
}): ImportPlanItem[] {
  const { cashAccountId, rows, planned, settledHashes } = params;
  const used = new Set<string>();
  const items: ImportPlanItem[] = [];

  for (const row of rows) {
    const hash = dedupHash({
      cashAccountId,
      date: row.date,
      direction: row.direction,
      amount: row.amount,
      description: row.description,
    });

    if (settledHashes.has(hash)) {
      items.push({ row, kind: 'duplicate', candidates: [], hash });
      continue;
    }

    const candidates = findCandidates(row, planned).filter((c) => !used.has(c.id));

    if (candidates.length === 1 && candidates[0]) {
      used.add(candidates[0].id);
      items.push({ row, kind: 'auto-match', candidates, hash, chosenEntryId: candidates[0].id });
    } else if (candidates.length > 1) {
      items.push({ row, kind: 'needs-review', candidates, hash, chosenEntryId: candidates[0]?.id ?? null });
    } else {
      items.push({ row, kind: 'new', candidates: [], hash, chosenEntryId: null });
    }
  }

  return items;
}

export function summarizePlan(items: ImportPlanItem[]) {
  return {
    total: items.length,
    autoMatched: items.filter((i) => i.kind === 'auto-match').length,
    needsReview: items.filter((i) => i.kind === 'needs-review').length,
    inserted: items.filter((i) => i.kind === 'new').length,
    duplicates: items.filter((i) => i.kind === 'duplicate').length,
  };
}
